import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Save, Upload, ShieldCheck, Loader2, Trash2, CheckCircle2,
  Brain, ClipboardList, Stethoscope, ClipboardCheck, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * Painel pós-consulta — profissional grava o prontuário (SOAP), anexa laudos/
 * atestados/receitas/relatórios e assina digitalmente. Também mostra o
 * resumo da triagem IA do caso vinculado (sem PII de contato).
 *
 * Uso: <PostConsultationPanel bookingId={b.id} readOnly={role !== "profissional"} />
 */
const DOC_KINDS = [
  { id: "laudo", label: "Laudo clínico" },
  { id: "atestado", label: "Atestado" },
  { id: "receita", label: "Receita / Prescrição" },
  { id: "relatorio", label: "Relatório de consulta" },
  { id: "encaminhamento", label: "Encaminhamento" },
  { id: "exame", label: "Exame" },
  { id: "outro", label: "Outro" },
];

const emptySummary = () => ({
  subjective: "",
  objective: "",
  assessment: "",
  plan: "",
  notes: "",
  follow_up_at: "",
});

export default function PostConsultationPanel({ bookingId, readOnly = false }) {
  const [summary, setSummary] = useState(emptySummary());
  const [context, setContext] = useState(null);
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signingId, setSigningId] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [docKind, setDocKind] = useState("relatorio");
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, d, c] = await Promise.all([
        api.get(`/bookings/${bookingId}/summary`).then((r) => r.data).catch(() => null),
        api.get(`/bookings/${bookingId}/documents`).then((r) => r.data).catch(() => []),
        api.get(`/bookings/${bookingId}/case-context`).then((r) => r.data).catch(() => null),
      ]);
      if (s) {
        setSummary({
          subjective: s.subjective || "",
          objective: s.objective || "",
          assessment: s.assessment || "",
          plan: s.plan || "",
          notes: s.notes || "",
          follow_up_at: s.follow_up_at || "",
        });
      }
      setDocs(Array.isArray(d) ? d : []);
      setContext(c);
    } catch {
      /* silent */
    }
  }, [bookingId]);

  useEffect(() => {
    if (expanded) loadAll();
  }, [expanded, loadAll]);

  const saveSummary = async () => {
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/summary`, summary);
      toast.success("Prontuário salvo.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar prontuário");
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(
        `/bookings/${bookingId}/documents?kind=${docKind}&title=${encodeURIComponent(docTitle || f.name)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Documento anexado.");
      setDocTitle("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const signDoc = async (docId) => {
    if (!window.confirm("Assinar digitalmente este documento? A assinatura registra seu nome, conselho, data e hash SHA-256, e não pode ser desfeita.")) return;
    setSigningId(docId);
    try {
      await api.post(`/bookings/${bookingId}/documents/${docId}/sign`);
      toast.success("Documento assinado digitalmente.");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao assinar");
    } finally {
      setSigningId(null);
    }
  };

  const openDoc = (docId) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/bookings/${bookingId}/documents/${docId}/download`, "_blank", "noopener");
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/bookings/${bookingId}/consultation-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prontuario-${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar PDF");
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`post-consult-toggle-${bookingId}`}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-bold text-[#0B2B62]">
            Prontuário &amp; documentos da consulta
          </span>
          {docs.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">
              {docs.length} doc{docs.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <span className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}>▾</span>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 p-4 space-y-5">
          {/* Triagem IA vinculada */}
          {context && context.available ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid={`post-consult-triage-${bookingId}`}>
              <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-violet-700 font-bold">
                <Brain className="w-3.5 h-3.5" />
                Triagem inteligente da LIA
                {context.protocol && (
                  <span className="text-slate-500 normal-case tracking-normal font-mono">· {context.protocol}</span>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs text-slate-700">
                {context.patient_age != null && <div><b>Idade:</b> {context.patient_age}</div>}
                {context.city && <div><b>Cidade:</b> {context.city}</div>}
                {context.substance && <div><b>Substância:</b> {context.substance}</div>}
                {context.family_intent && <div><b>Intenção:</b> {context.family_intent}</div>}
                {context.risk_level && <div><b>Risco:</b> {context.risk_level}</div>}
              </div>
              {context.situation && (
                <div className="mt-2 text-xs text-slate-800 leading-relaxed">
                  <b>Situação relatada:</b> {context.situation}
                </div>
              )}
              {context.consultation_context && (
                <div className="mt-2 text-xs text-slate-700 space-y-1">
                  {context.consultation_context.professional_type && <div><b>Tipo desejado:</b> {context.consultation_context.professional_type}</div>}
                  {context.consultation_context.consultation_reason && <div><b>Objetivo:</b> {context.consultation_context.consultation_reason}</div>}
                  {context.consultation_context.previous_treatment && <div><b>Tratamentos anteriores:</b> {context.consultation_context.previous_treatment}</div>}
                  {context.consultation_context.medications && <div><b>Medicações:</b> {context.consultation_context.medications}</div>}
                </div>
              )}
              {context.intake_summary && (
                <div className="mt-2 p-2 rounded bg-white border border-violet-100 text-xs text-slate-700 whitespace-pre-wrap break-words">
                  {typeof context.intake_summary === "string"
                    ? context.intake_summary
                    : JSON.stringify(context.intake_summary, null, 2)}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic">Triagem IA não disponível para esta reserva.</div>
          )}

          {/* Prontuário SOAP */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-widest text-[#0B2B62] font-bold">
              <Stethoscope className="w-3.5 h-3.5" />
              Prontuário (SOAP)
            </div>
            <div className="grid gap-3">
              <Field label="S — Subjetivo (queixas do paciente)" testid={`soap-subjective-${bookingId}`}
                value={summary.subjective} readOnly={readOnly}
                onChange={(v) => setSummary({ ...summary, subjective: v })} />
              <Field label="O — Objetivo (observações clínicas)" testid={`soap-objective-${bookingId}`}
                value={summary.objective} readOnly={readOnly}
                onChange={(v) => setSummary({ ...summary, objective: v })} />
              <Field label="A — Avaliação / Impressão diagnóstica" testid={`soap-assessment-${bookingId}`}
                value={summary.assessment} readOnly={readOnly}
                onChange={(v) => setSummary({ ...summary, assessment: v })} />
              <Field label="P — Plano terapêutico" testid={`soap-plan-${bookingId}`}
                value={summary.plan} readOnly={readOnly}
                onChange={(v) => setSummary({ ...summary, plan: v })} />
              <Field label="Notas internas (opcional)" testid={`soap-notes-${bookingId}`}
                value={summary.notes} readOnly={readOnly}
                onChange={(v) => setSummary({ ...summary, notes: v })} rows={2} />
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  Sugestão de retorno (opcional)
                </label>
                <input
                  type="datetime-local"
                  disabled={readOnly}
                  value={summary.follow_up_at || ""}
                  onChange={(e) => setSummary({ ...summary, follow_up_at: e.target.value })}
                  data-testid={`soap-followup-${bookingId}`}
                  className="mt-1 w-full sm:w-64 px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </div>
              {!readOnly && (
                <div className="flex justify-end">
                  <button
                    onClick={saveSummary}
                    disabled={saving}
                    data-testid={`soap-save-${bookingId}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Salvar prontuário
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Documentos anexados */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#0B2B62] font-bold">
                <FileText className="w-3.5 h-3.5" />
                Documentos ({docs.length})
              </div>
              <button onClick={downloadPdf} data-testid={`consult-pdf-${bookingId}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#0B2B62] text-[#0B2B62] hover:bg-[#0B2B62] hover:text-white text-xs font-bold transition-colors">
                <FileText className="w-3.5 h-3.5" /> Baixar prontuário (PDF)
              </button>
            </div>

            {docs.length === 0 && (
              <div className="text-xs text-slate-500 italic mb-3">
                Nenhum documento anexado ainda.
              </div>
            )}

            {docs.length > 0 && (
              <ul className="space-y-2 mb-4" data-testid={`consult-docs-list-${bookingId}`}>
                {docs.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-md p-2 text-xs bg-slate-50">
                    <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{d.title || d.filename}</div>
                      <div className="text-[10px] text-slate-500 flex flex-wrap gap-2">
                        <span className="uppercase tracking-widest">{d.kind_label}</span>
                        <span>{Math.round((d.size || 0) / 1024)} KB</span>
                        {d.signature ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                            <ShieldCheck className="w-3 h-3" />
                            Assinado por {d.signature.signer_name}
                            {d.signature.signer_council ? ` · ${d.signature.signer_council}` : ""} em{" "}
                            {new Date(d.signature.signed_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        ) : (
                          <span className="text-amber-700">Não assinado</span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => openDoc(d.id)} data-testid={`consult-doc-open-${d.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 hover:bg-white text-[11px] text-slate-700">
                      <ExternalLink className="w-3 h-3" /> Abrir
                    </button>
                    {!readOnly && !d.signature && (
                      <button onClick={() => signDoc(d.id)} disabled={signingId === d.id}
                        data-testid={`consult-doc-sign-${d.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#0B2B62] hover:bg-[#0a2454] text-white text-[11px] font-bold disabled:opacity-50">
                        {signingId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardCheck className="w-3 h-3" />}
                        Assinar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {!readOnly && (
              <div className="border-t border-slate-100 pt-3">
                <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Tipo</label>
                    <select value={docKind} onChange={(e) => setDocKind(e.target.value)}
                      data-testid={`consult-doc-kind-${bookingId}`}
                      className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
                      {DOC_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Título</label>
                    <input value={docTitle} onChange={(e) => setDocTitle(e.target.value)}
                      placeholder="Ex.: Laudo psicológico"
                      data-testid={`consult-doc-title-${bookingId}`}
                      className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
                  </div>
                  <button type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid={`consult-doc-upload-${bookingId}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold disabled:opacity-50">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Anexar
                  </button>
                  <input ref={fileRef} hidden type="file"
                    accept=".pdf,image/png,image/jpeg,image/webp"
                    onChange={uploadDoc} />
                </div>
                <p className="mt-2 text-[10px] text-slate-500">
                  Aceita PDF, PNG, JPG ou WEBP (máx. 15 MB). Após anexar, clique em <b>Assinar</b> para carimbar
                  digitalmente com seu nome, conselho e hash SHA-256.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, readOnly, rows = 3, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
    <textarea
      rows={rows}
      readOnly={readOnly}
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      data-testid={testid}
      className={`mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm resize-none ${readOnly ? "bg-slate-50" : ""}`}
    />
  </div>
);
